/**
 * The guided step limit as the screen shows it (P4-D12, P5-D8): a stepped decision (the dividend, the rate position) moves at most N grid steps a year in a guided round. The rule is the api guard's own
 * (`guidedStepRange` / `guidedStepExceeds` in the domain), so a pick the screen offers is a pick the server saves. The limit rides the existing bound machinery (`DecisionBound`): the control clamps a pick to it and says so before the click.
 */
import { decisionOfPath, guidedStepExceeds, guidedStepRange, type Decisions } from "@bsim/domain";
import type { ContentCatalog, DecisionCatalogEntry } from "@bsim/contracts";
import type { DecisionBound } from "./DecisionControl";

const limitOf = (catalog: ContentCatalog, complexity: string, decisionId: string): number | undefined =>
  complexity === "guided" ? (catalog.guided?.stepLimit as Partial<Record<string, number>> | undefined)?.[decisionId] : undefined;

/** The caps and floors the guided step limit puts on one decision's numeric sub-fields this round. */
export function guidedStepBounds(entry: DecisionCatalogEntry, catalog: ContentCatalog, complexity: string, seed: Decisions | undefined): readonly DecisionBound[] {
  const limit = limitOf(catalog, complexity, entry.decisionId);
  if (limit === undefined) return [];
  const line = `In guided play this moves at most ${limit} step${limit === 1 ? "" : "s"} a year from where it stands now`;
  return Object.keys(entry.options).flatMap((sub): DecisionBound[] => {
    const range = guidedStepRange(sub === "" ? entry.decisionId : `${entry.decisionId}.${sub}`, entry.decisionId, seed, limit);
    return range === undefined ? [] : [{ sub, kind: "min", value: range.min, line }, { sub, kind: "max", value: range.max, line }];
  });
}

/** The sentence for a pick past the limit that the bounds cannot clamp (a named setting such as "No hedge"); null when the patch is inside it. */
export function guidedStepRefusal(patch: Readonly<Record<string, unknown>>, catalog: ContentCatalog, complexity: string, seed: Decisions | undefined): string | null {
  for (const [path, value] of Object.entries(patch)) {
    const id = decisionOfPath(path), limit = limitOf(catalog, complexity, id);
    if (limit !== undefined && guidedStepExceeds(path, id, value, seed, limit)) return `In guided play this decision moves at most ${limit} step${limit === 1 ? "" : "s"} a year, counted from where it stands now; larger moves open in the full game.`;
  }
  return null;
}
